import { Button } from "components/DesignSystem/Button/Button";
import { FileIcon } from "components/design/FileIcon";
import { DownloadIconV2 } from "components/icons/DownloadIconV2";
import { Preview } from "components/PreviewModal";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { KeyboardEvent, useState } from "react";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { BackendError } from "types/utils/error";
import { fileTypeFromName } from "util/fileTypeFromName";
import { openDownloadUrl } from "util/openDownloadUrl";
import { DocumentFile } from "v2/store/entityDocumentsApi";

type InkleFileRowProps = {
  file: DocumentFile;
};

// Read-only counterpart of FileRow (Inkle-uploaded docs): view + download only,
// no replace/delete.
export const InkleFileRow = ({ file }: InkleFileRowProps) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { alertToast } = useToast();
  const [showPreview, setShowPreview] = useState(false);

  const [fetchPreviewUrl, { isFetching: isDownloading }] =
    useLazyGetPreviewUrlQuery();

  const iconType = file.fileType || fileTypeFromName(file.name);

  const handleViewClick = (): void => setShowPreview(true);
  const handleClosePreview = (): void => setShowPreview(false);

  const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>): void => {
    if (event.key === "Enter" || event.key === " ") handleViewClick();
  };

  const downloadFile = async (): Promise<void> => {
    try {
      const { download_url } = await fetchPreviewUrl({
        groupId,
        fileId: file.uuid,
      }).unwrap();
      if (download_url) openDownloadUrl(download_url);
    } catch (e) {
      alertToast({
        message:
          (e as BackendError)?.data?.error?.message ??
          "Couldn't download the document",
      });
    }
  };

  const handleDownload = (): void => void downloadFile();

  return (
    <div className="t-flex t-items-center t-gap-2">
      <span
        role="button"
        tabIndex={0}
        onClick={handleViewClick}
        onKeyDown={handleKeyDown}
        className="t-flex t-min-w-0 t-flex-1 t-cursor-pointer t-items-center t-gap-1.5 t-text-body-sm t-text-text-30 hover:t-text-text-100"
      >
        <span className="t-flex t-shrink-0">
          <FileIcon fileType={iconType} size="20" />
        </span>
        <span className="t-truncate">{file.name}</span>
      </span>

      <Button
        size="small"
        onClick={handleDownload}
        isLoading={isDownloading}
        aria-label="Download document"
      >
        <DownloadIconV2 className="t-text-text-30" />
      </Button>

      <Preview
        showModal={showPreview}
        closeModal={handleClosePreview}
        previewId={showPreview ? file.uuid : null}
      />
    </div>
  );
};
